import React, { useCallback, useRef, useState, useEffect } from 'react';
import { getDisplayedPlaybackTime } from '@/utils/timeUtils';

interface ProgressBarProps {
  duration: number;
  currentTime: number;
  onSeek: (time: number) => void;
  isPlaying: boolean;
  audioRef: React.RefObject<HTMLAudioElement | null>;
  className?: string;
  containerClassName?: string;
  useLargeHitArea?: boolean;
  imperativePlayback?: boolean;
  /** Saved offset not yet applied to the element; shown in its place meanwhile. */
  pendingResumeSeconds?: number | null;
}

const ProgressBar: React.FC<ProgressBarProps> = ({ duration, currentTime, onSeek, isPlaying, audioRef, className, containerClassName, useLargeHitArea = false, imperativePlayback = false, pendingResumeSeconds = null }) => {
  const progressBarRef = useRef<HTMLDivElement>(null);
  const fillRef = useRef<HTMLDivElement>(null);
  const [isSeeking, setIsSeeking] = useState(false);
  const [seekPercentage, setSeekPercentage] = useState<number | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const wasPlayingRef = useRef(false);
  const interactionTimer = useRef<NodeJS.Timeout | null>(null);

  // The rendered fill and the imperative one below must agree on the time, or
  // each re-render and each audio event overwrite the other's transform.
  // Read the live element rather than the `currentTime` prop: ReadPlayer is
  // memoized against a comparator that deliberately skips re-rendering on
  // currentTime-only changes (so continuous playback doesn't re-render the
  // whole player), so a seek while paused can leave that prop stale for a
  // while — same reason the imperative fill below reads off the element too.
  const playbackTime = getDisplayedPlaybackTime(audioRef.current, currentTime, pendingResumeSeconds);
  const playbackPercentage = duration > 0 ? (playbackTime / duration) * 100 : 0;
  // Keep showing the dragged position through the release animation (isDragging
  // stays true after mouseup/touchend while it plays out) instead of snapping
  // back to playbackPercentage before currentTime has caught up to the seek.
  const displayPercentage = (isSeeking || isDragging) && seekPercentage !== null ? seekPercentage : playbackPercentage;

  const updateImperativeFill = useCallback(() => {
    if (!fillRef.current || duration <= 0) {
      return;
    }

    const audioTime = getDisplayedPlaybackTime(audioRef.current, currentTime, pendingResumeSeconds);
    const percentage = Math.max(0, Math.min(100, (audioTime / duration) * 100));
    fillRef.current.style.transform = `scaleX(${percentage / 100})`;
  }, [audioRef, currentTime, duration, pendingResumeSeconds]);

  useEffect(() => {
    if (!imperativePlayback || isSeeking) {
      return;
    }

    const audio = audioRef.current;
    let intervalId: number | null = null;

    const stopInterval = () => {
      if (intervalId !== null) {
        window.clearInterval(intervalId);
        intervalId = null;
      }
    };

    const startInterval = () => {
      updateImperativeFill();
      if (!audio || audio.paused || audio.ended) {
        stopInterval();
        return;
      }
      if (intervalId === null) {
        intervalId = window.setInterval(updateImperativeFill, 100);
      }
    };

    updateImperativeFill();
    startInterval();

    audio?.addEventListener('play', startInterval);
    audio?.addEventListener('playing', startInterval);
    audio?.addEventListener('pause', stopInterval);
    audio?.addEventListener('ended', stopInterval);
    audio?.addEventListener('seeked', updateImperativeFill);
    audio?.addEventListener('timeupdate', updateImperativeFill);
    audio?.addEventListener('loadedmetadata', updateImperativeFill);

    return () => {
      stopInterval();
      audio?.removeEventListener('play', startInterval);
      audio?.removeEventListener('playing', startInterval);
      audio?.removeEventListener('pause', stopInterval);
      audio?.removeEventListener('ended', stopInterval);
      audio?.removeEventListener('seeked', updateImperativeFill);
      audio?.removeEventListener('timeupdate', updateImperativeFill);
      audio?.removeEventListener('loadedmetadata', updateImperativeFill);
    };
  }, [audioRef, imperativePlayback, isSeeking, updateImperativeFill]);

  const handleSeekInteraction = useCallback((clientX: number, isFinal: boolean) => {
    if (!progressBarRef.current || duration <= 0) return;

    const rect = progressBarRef.current.getBoundingClientRect();
    // 99.98 rather than 100 is a temporary fix
    // for issue that doesn't immediately transition to next chapter when dragging scrubber/seeker to 100%
    const percentage = Math.max(0, Math.min(99.98, ((clientX - rect.left) / rect.width) * 100));
    
    if (isFinal) {
      const newTime = (percentage / 100) * duration;
      onSeek(newTime);
      setIsSeeking(false);
      // seekPercentage is cleared later, once the release animation finishes,
      // so the dot doesn't snap to the stale currentTime before it catches up.
      if (audioRef.current && wasPlayingRef.current) {
        // Add a small delay to ensure the click event is processed before playing
        setTimeout(() => audioRef.current?.play().catch(console.error), 50);
      }
    } else {
      setSeekPercentage(percentage);
    }
  }, [duration, onSeek, audioRef]);


  const handleMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
    wasPlayingRef.current = isPlaying;
    
    // Delay pausing to differentiate between click and drag
    interactionTimer.current = setTimeout(() => {
      if (audioRef.current && wasPlayingRef.current) {
        audioRef.current.pause();
      }
    }, 150); // 150ms delay

    setIsSeeking(true);
    setIsDragging(false); // Reset dragging state on new interaction
    handleSeekInteraction(event.clientX, false);
  };

  const handleTouchStart = (event: React.TouchEvent<HTMLDivElement>) => {
    wasPlayingRef.current = isPlaying;
    if (audioRef.current && isPlaying) {
      audioRef.current.pause();
    }
    setIsSeeking(true);
    setIsDragging(false); // Reset dragging state on new interaction
    handleSeekInteraction(event.touches[0].clientX, false);
  };

  useEffect(() => {
    const handleMouseMove = (event: MouseEvent) => {
      if (isSeeking) {
        if (!isDragging) {
          setIsDragging(true);
          setIsAnimating(true);
        }
        handleSeekInteraction(event.clientX, false);
      }
    };
    const handleTouchMove = (event: TouchEvent) => {
      if (isSeeking) {
        if (!isDragging) {
          setIsDragging(true);
          setIsAnimating(true);
        }
        handleSeekInteraction(event.touches[0].clientX, false);
      }
    };

    const handleMouseUp = (event: MouseEvent) => {
      if (isSeeking) {
        if (interactionTimer.current) {
          clearTimeout(interactionTimer.current);
        }
        handleSeekInteraction(event.clientX, true);
        setIsAnimating(false);
        setTimeout(() => {
          setIsDragging(false);
          setSeekPercentage(null);
        }, 500); // Allow animation to finish
      }
    };
    const handleTouchEnd = (event: TouchEvent) => {
      if (isSeeking) {
        const finalClientX = event.changedTouches[0]?.clientX;
        if (finalClientX) {
          handleSeekInteraction(finalClientX, true);
        } else {
          // Fallback if changedTouches is not available
          setIsSeeking(false);
          setSeekPercentage(null);
          if (audioRef.current && wasPlayingRef.current) {
            audioRef.current.play().catch(console.error);
          }
        }
        setIsAnimating(false);
        setTimeout(() => {
          setIsDragging(false);
          setSeekPercentage(null);
        }, 500); // Allow animation to finish
      }
    };

    if (isSeeking) {
      document.body.classList.add('select-none');
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      window.addEventListener('touchmove', handleTouchMove);
      window.addEventListener('touchend', handleTouchEnd);
    } else {
      document.body.classList.remove('select-none');
    }

    return () => {
      document.body.classList.remove('select-none');
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isSeeking, handleSeekInteraction, audioRef]);

  const handleProgressBarClick = (event: React.MouseEvent<HTMLDivElement>) => {
    // Prevent click from firing during or at the end of a drag
    if (event.detail === 1) { // Ensures it's a real click
       handleSeekInteraction(event.clientX, true);
    }
  };

  return (
    <div
      ref={progressBarRef}
      className={`w-full ${useLargeHitArea ? 'h-10' : 'h-2'} relative flex items-center ${containerClassName ?? ''}`} // <-- MUST have 'flex' and 'items-center'
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}
      onClick={handleProgressBarClick}
    >
      <div
        className={`w-full h-2 bg-blue-200 cursor-pointer relative overflow-hidden ${className}`} // <-- MUST be 'h-2'
      >
        <div
          ref={fillRef}
          className={`h-full w-full origin-left bg-blue-700 ${!isSeeking && !imperativePlayback ? 'transition-transform duration-100 ease-linear' : ''}`}
          style={{ transform: `scaleX(${Math.max(0, Math.min(100, displayPercentage)) / 100})` }}
        />
      </div>
      {isDragging && (
        <div
          className={`absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-4 h-4 bg-blue-500 rounded-full pointer-events-none transition-transform duration-500 ${isAnimating ? 'scale-100' : 'scale-0'}`}
          style={{ left: `${displayPercentage}%` }}
        />
      )}
    </div>
  );
};

export default ProgressBar;
